import { getDiagnostic } from '@blk/aladdin-frontend-telemetry';
import { Date as GDate } from '@blk/aladdin-graph-everything/google/type/date_pb';
import { OperatingSystem } from '@blk/aladdin-graph-everything/platform/common/enums/operating_system_pb';
import {
  AWEEvent,
  Application,
  DomainSpecificInfo,
  Element as ElementInfo,
  Error,
  Feature,
  Session,
  User,
} from '@blk/aladdin-graph-everything/platform/event_logging/awe_event/v1/awe_event_pb';
import { EventLoggingDiagnostic } from '@blk/aladdin-graph-everything/platform/event_logging/event_logging_base/v1/event_logging_pb';
import { SENSITIVE_DATA_BRANDED_TYPE, logPayload } from '@blk/aw-api-clients';
import { TrackingPayload } from '@blk/aw-bff-typescript-models';
import { ExcelExportParams, GridApi, ProcessCellForExportParams } from 'ag-grid-community';
import { isEmpty, isNil, isUndefined } from 'lodash';
import get from 'lodash/get';
import type { MouseEvent as ReactMouseEvent } from 'react';
import {
  AW_ADVISOR_APP_NAME,
  BACKGROUND_EVENTS,
  BACKGROUND_EVENTS_TAG,
  CustomLogAttribute,
  DEBUG_ON_PARAM,
  ELEMENT_STATE_CHECKED,
  ELEMENT_STATE_UNCHECKED,
  LOGGER_CONTEXT_NAME,
  LogAction,
  MASKED_SENSITIVE_VALUE,
  TrackedAction,
} from '../constants';
import { LoggerDataContext, LoggerDataContextType } from '../context';
import { AppMetadata, EventType, EventTypeMap, TrackingData } from '../models';

type EventData = { disableTrackEvent?: boolean };

type AuxElementCustomData = {
  label?: string;
  eventData?: EventData;
};

// Extended type of element to include additional properties for logging
export interface ElementNode extends Element {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any type is used to support custom attribute data, which could contain any value based on the implementation
  [key: string]: any;
  data?: { [key: number]: { label?: string } };
  displayValue?: string;
  icon?: string;
  label?: string;
  memoizedProps?: { [key: string]: unknown };
  's-hn'?: string;
  stateNode?: { isDisabled?: boolean; props?: { isDisabled?: boolean; data?: AuxElementCustomData[][] } };
}
export const LoggerUtil = {
  /**
   * Translates the object based on the mappings provided
   * @param obj
   * @param mappings: [key] as a target object key and value as a source object key
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any type is used to support various types of objects
  translateObject: (obj: any, mappings: { [key: string]: string }): { [key: string]: string } => {
    let translatedObj: { [key: string]: string } = {};
    Object.entries(mappings).forEach(([key, value]) => {
      if (!!get(obj, value)) {
        translatedObj[key] = get(obj, value, '');
      }
    });
    return translatedObj;
  },

  /**
   * Checks if debug mode is enabled, set URL param debugOn=true to enable debug mode
   * @returns boolean
   */
  isDebugModeEnabled: (): boolean => LoggerUtil.getParamFromUrl(DEBUG_ON_PARAM) === 'true',

  getTagFromEventMetadata: (metadata: unknown): string | null => {
    if (!metadata || typeof metadata !== 'object') {
      return null;
    }

    const tag = (metadata as { tag?: unknown }).tag;
    return typeof tag === 'string' ? tag : null;
  },

  /**
   * Computes metadata for the event
   * @param trackingData Tracking data for the event
   * @param appMetadata App metadata for the event
   * @returns Metadata for the event
   */
  computeMetadata: (trackingData: TrackingData): string | undefined => {
    const metadata = trackingData.elementInfo?.metadata;
    // Check if any conditions require metadata modification
    const shouldAddTag = BACKGROUND_EVENTS.includes(trackingData.action);
    // If no conditions are met, return original metadata
    if (!shouldAddTag) {
      return metadata;
    }
    try {
      const metadataParsed: Record<string, unknown> = JSON.parse(metadata ?? '{}');
      // Set tag info for filtering
      if (shouldAddTag) {
        metadataParsed.tag = BACKGROUND_EVENTS_TAG;
      }
      return JSON.stringify(metadataParsed);
    } catch (error) {
      console.warn('Error parsing metadata, incorrect JSON provided under elementInfo.metadata', error);
    }
    return metadata;
  },

  // Set element info
  setElementInfo: (event: AWEEvent, trackingData: TrackingData): void => {
    const element = new ElementInfo();
    const srcElement = trackingData.srcElement as ElementNode;
    element.setElementNameLocal(LoggerUtil.getElementNameLocal(trackingData, srcElement));
    element.setElementName(LoggerUtil.getElementName(trackingData, element.getElementNameLocal()));
    element.setElementId(
      trackingData.elementInfo?.elementId ||
        srcElement?.getAttribute('data-testid') ||
        srcElement?.id ||
        srcElement?.getAttribute('class') ||
        ''
    );
    const tagName = srcElement?.tagName ?? '';
    const type = srcElement?.type ? `:${srcElement?.type}` : '';
    const elementTypeFromTagname = `${tagName}${type}`;
    element.setElementType(trackingData.elementInfo?.elementType || elementTypeFromTagname);
    element.setEvent(trackingData.elementInfo?.event || trackingData.action);
    element.setEventType(
      trackingData.elementInfo?.eventType ?? EventTypeMap[trackingData.srcEvent?.type ?? ''] ?? EventType.EVENT_CLICK
    );
    element.setElementValue((trackingData.elementInfo?.elementValue ?? '').trim());
    const metadata = LoggerUtil.computeMetadata(trackingData);
    if (!isNil(metadata)) {
      element.setMetadata(metadata);
    }
    event.setElement(element);
  },

  // Extract element name local
  getElementNameLocal: (trackingData: TrackingData, srcElement: ElementNode | null): string =>
    LoggerUtil.getMaskSensitiveData(
      (
        trackingData.elementInfo?.elementNameLocal ||
        srcElement?.textContent ||
        srcElement?.label ||
        srcElement?.displayValue ||
        srcElement?.icon ||
        ''
      ).trim()
    ),

  // Extract element name
  getElementName: (trackingData: TrackingData, elementNameLocal: string): string => {
    const elementName = trackingData.elementInfo?.elementName;
    const elementMetadata = trackingData.elementInfo?.elementMetadata;

    let combinedElementName = '';
    if (elementName) {
      combinedElementName = elementName;
    } else if (elementNameLocal) {
      combinedElementName = elementMetadata ? `${elementMetadata}:${elementNameLocal}` : elementNameLocal;
    }

    return LoggerUtil.getMaskSensitiveData(combinedElementName);
  },

  getMaskSensitiveData: (value: string): string =>
    LoggerUtil.containsSensitiveData(value) ? MASKED_SENSITIVE_VALUE : value,

  containsSensitiveData: (value: string): boolean => value.includes(SENSITIVE_DATA_BRANDED_TYPE),

  getAppName: (appMetadata: AppMetadata): string =>
    appMetadata.applicationNameFn?.() ?? (appMetadata.applicationName || AW_ADVISOR_APP_NAME),

  // Set app info
  setAppInfo: (event: AWEEvent, trackingData: TrackingData, appMetadata: AppMetadata): void => {
    const appInfo = new Application();
    appInfo.setAppName(LoggerUtil.getAppName(appMetadata));
    appInfo.setAppView(trackingData.activeView ?? '');
    appInfo.setAppTab(trackingData.activeTab ?? '');
    appInfo.setAppMode(appMetadata.appMode ?? '');
    appInfo.setComponentName(trackingData.componentName ?? '');
    appInfo.setPageUri(window.location.href);
    appInfo.setAppDomain(LoggerUtil.getDomainName());
    event.setApp(appInfo);
  },

  // Set session info
  setSessionInfo: (event: AWEEvent, appMetadata: AppMetadata, sessionId: string): void => {
    const sessionInfo = new Session();
    sessionInfo.setSessionId(sessionId);
    if (!isUndefined(appMetadata.analysisDate) && !isEmpty(appMetadata.analysisDate)) {
      const analysisDate = new Date(appMetadata.analysisDate);
      const analysisGDate = new GDate();
      analysisGDate.setDay(analysisDate.getDate());
      analysisGDate.setMonth(analysisDate.getMonth() + 1);
      analysisGDate.setYear(analysisDate.getFullYear());
      sessionInfo.setAnalysisDate(analysisGDate);
    }
    sessionInfo.setPkgName(appMetadata.pkg || LoggerUtil.getParamFromUrl('pkg'));
    event.setSession(sessionInfo);
  },

  // Set user info
  setUserInfo: (event: AWEEvent, appMetadata: AppMetadata): void => {
    const userInfo = new User();
    const isInternalUser = appMetadata.internalUserFn?.();
    if (!isUndefined(isInternalUser)) {
      userInfo.setInternalUser(isInternalUser);
    }
    userInfo.setLanguagePreference(appMetadata.languagePreferenceFn?.() ?? '');
    userInfo.setShowingMyBook(appMetadata.showingMyBook ?? false);
    userInfo.setViewAsId(appMetadata.viewAsIdFn?.() ?? '');
    event.setUser(userInfo);
  },

  // Set feature info
  setFeatureInfo: (event: AWEEvent, trackingData: TrackingData, appMetadata: AppMetadata): void => {
    const featureInfo = new Feature();
    featureInfo.setFeatureName(trackingData.featureInfo?.featureName || LoggerUtil.getFeatureNameFromUrl(appMetadata));
    featureInfo.setFeatureLocal(trackingData.featureInfo?.featureLocal ?? '');
    featureInfo.setSectionName(trackingData.featureInfo?.sectionName ?? '');
    featureInfo.setSectionLocal(trackingData.featureInfo?.sectionLocal ?? '');
    event.setFeature(featureInfo);
  },

  // set error info
  setErrorInfo: (event: AWEEvent, trackingData: TrackingData): void => {
    if (trackingData?.error) {
      const error = new Error();
      error.setRequestId(trackingData.error.requestId ?? '');
      error.setErrorInfo(trackingData.error.errorInfo ?? '');
      event.setError(error);
    }
  },

  // set domain specific info
  setDomainSpecificInfo: (event: AWEEvent, trackingData: TrackingData): void => {
    if (trackingData.domainSpecificInfo) {
      const domainSpecificInfo = new DomainSpecificInfo();
      domainSpecificInfo.setAccountId(trackingData.domainSpecificInfo.accountId ?? '');
      domainSpecificInfo.setAccountType(trackingData.domainSpecificInfo.accountType ?? '');
      domainSpecificInfo.setProposalId(trackingData.domainSpecificInfo.proposalId ?? '');
      domainSpecificInfo.setProposalType(trackingData.domainSpecificInfo.proposalType ?? '');
      domainSpecificInfo.setAum(Math.round(trackingData.domainSpecificInfo.aum ?? 0));
      domainSpecificInfo.setProposedAum(Math.round(trackingData.domainSpecificInfo.proposedAum ?? 0));
      domainSpecificInfo.setCurrencyCode(trackingData.domainSpecificInfo.currencyCode ?? '');
      domainSpecificInfo.setReportId(trackingData.domainSpecificInfo.reportId ?? '');
      if (!isEmpty(trackingData.domainSpecificInfo.additionalAttributes)) {
        Object.entries(trackingData.domainSpecificInfo.additionalAttributes).forEach(([key, value]) => {
          domainSpecificInfo.getAdditionalAttributesMap().set(key, value);
        });
      }
      event.setDomainSpecificInfo(domainSpecificInfo);
    }
  },

  getHashCode: (val: string): number => {
    var hash = 0;
    for (var i = 0; i < val.length; i++) {
      var char = val.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash;
  },

  /**
   * Returns tracking data based on the event(helper function for default tracking)
   * @param e -- event
   * @returns TrackingData | undefined
   * @tutorial wiki: https://webster.bfm.com/Wiki/display/AFWM/AW+Event+Logging+%28Web%29+-+Developer+Guide#AWEventLogging(Web)DeveloperGuide-Defaulttracking
   */
  getTrackingData: (
    e: MouseEvent | ReactMouseEvent,
    interactiveElementSelectors?: string[]
  ): TrackingData | undefined => {
    let element = e.target as ElementNode;
    const reactFiber = LoggerUtil.getReactFiber(element);
    if (!LoggerUtil.isTrackingDisabled(element, reactFiber)) {
      const contextInfo: LoggerDataContextType = LoggerUtil.findContextInFiber(
        reactFiber,
        LoggerDataContext.displayName || LOGGER_CONTEXT_NAME
      );
      /*
       * extract custom attributes which will be used to override the default tracking
       * wiki: https://webster.bfm.com/Wiki/display/AFWM/AW+Event+Logging+%28Web%29+-+Developer+Guide#AWEventLogging(Web)DeveloperGuide-Addingspecificinformationtodefaulttrackedevents
       */
      const nodeWithCustomAttributes = LoggerUtil.getElementWithAttributes(element, reactFiber, [
        CustomLogAttribute.TRACK_ACTION,
        CustomLogAttribute.TRACK_ELEMENT_NAME,
      ]);
      if (LoggerUtil.isClickableElement(element, reactFiber, interactiveElementSelectors) || nodeWithCustomAttributes) {
        let elementLabel;
        let elementValue;
        // special cases to extract the right element info
        switch (element.tagName?.toLowerCase()) {
          case 'aux-checkbox-group':
          case 'aux-radio-group':
            if (element.shadowRoot?.activeElement) {
              element = element.shadowRoot.activeElement;
              elementValue = element.isChecked ? ELEMENT_STATE_CHECKED : ELEMENT_STATE_UNCHECKED;
            } else {
              return undefined;
            }
            break;
          case 'aux-checkbox':
          case 'aux-radio':
            elementValue = element.isChecked ? ELEMENT_STATE_CHECKED : ELEMENT_STATE_UNCHECKED;
            break;
          case 'aux-segmented-control':
            if (
              e.detail === 0 &&
              typeof element.activeControlIndex === 'number' &&
              element.data?.[element.activeControlIndex ?? 0]
            ) {
              elementLabel = element.data?.[element.activeControlIndex ?? 0]?.label;
            } else {
              return undefined;
            }
            break;
          case 'aux-toggle':
            // checked state gets toggled after click
            elementValue = !element.isChecked ? ELEMENT_STATE_CHECKED : ELEMENT_STATE_UNCHECKED;
            break;
          // special case for aux-type-ahead where span contains only a part of the label, so we need to get the parent element text content
          case 'span':
          case 'mark':
            if (element['s-hn']?.toLowerCase() === 'aux-type-ahead') {
              elementLabel = element.parentElement?.textContent;
            }
        }
        const auxIcon = element.closest('aux-icon');
        if (!!auxIcon) {
          element = auxIcon;
          elementLabel = element.nextElementSibling?.textContent;
        }
        const contextDataToLog = contextInfo
          ? {
              activeTab: contextInfo.activeTab,
              activeView: contextInfo.activeView,
              featureInfo: {
                featureName: contextInfo.feature,
                featureLocal: contextInfo.featureLocal,
                sectionName: contextInfo.section,
                sectionLocal: contextInfo.sectionLocal,
              },
              domainSpecificInfo: contextInfo?.domainSpecificInfo,
            }
          : {};
        // fetch the metadata even if it is not on the same level of other custom attributes
        const elMetadataNode = LoggerUtil.getElementWithAttributes(element, reactFiber, [
          CustomLogAttribute.TRACK_ELEMENT_METADATA,
        ]);
        const elementMetadata = LoggerUtil.getAttributeValue(elMetadataNode, CustomLogAttribute.TRACK_ELEMENT_METADATA);
        const metadataNode = LoggerUtil.getElementWithAttributes(element, reactFiber, [
          CustomLogAttribute.TRACK_METADATA,
        ]);
        const metadata = LoggerUtil.getAttributeValue(metadataNode, CustomLogAttribute.TRACK_METADATA);
        const eventType = EventTypeMap[e.type ?? ''] ?? EventType.EVENT_CLICK;
        return {
          ...contextDataToLog,
          action:
            (LoggerUtil.getAttributeValue(
              nodeWithCustomAttributes,
              CustomLogAttribute.TRACK_ACTION
            ) as TrackedAction) || LogAction.ELEMENT_CLICKED,
          srcEvent: e,
          srcElement: element,
          elementInfo: {
            eventType,
            ...(!!nodeWithCustomAttributes
              ? {
                  elementName:
                    LoggerUtil.getAttributeValue(nodeWithCustomAttributes, CustomLogAttribute.TRACK_ELEMENT_NAME) ?? '',
                }
              : {}),
            ...(!!elementLabel ? { elementNameLocal: elementLabel } : {}),
            ...(!!elementValue ? { elementValue } : {}),
            ...(!!elementMetadata ? { elementMetadata } : {}),
            ...(!!metadata ? { metadata } : {}),
          },
        };
      }
    }
    return undefined;
  },

  /**
   * Function to get react fiber from the element
   * @param element element to get react fiber
   * @returns reactFiber
   */
  getReactFiber: (element?: ElementNode): ElementNode | undefined => {
    let currentElement = element;
    while (currentElement) {
      const key = Object.keys(currentElement).find(
        (k) => k.startsWith('__reactInternalInstance$') || k.startsWith('__reactFiber$')
      );
      if (key) {
        return currentElement[key] as ElementNode;
      }
      currentElement = currentElement.hostRef ?? (currentElement.parentElement as ElementNode);
    }
    return undefined;
  },

  /**
   * Returns context value from react fiber.
   *
   * Supports both ref-based context (with getValues method) and legacy direct value contexts.
   * For ref-based contexts, calls getValues() to retrieve current values without causing re-renders.
   *
   * @param fiber react fiber
   * @param contextType string value of context type (e.g., 'LoggerDataContext')
   * @returns context value or undefined
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any type is used to support various types of context objects
  findContextInFiber: (fiber: ElementNode | undefined, contextType: string): any => {
    if (!fiber) {
      return undefined;
    }
    let currentFiber = fiber;
    while (currentFiber) {
      const fiberType: { _context?: { displayName?: string } } = currentFiber.type ?? {};
      if (fiberType._context?.displayName === contextType) {
        const contextValue = currentFiber.memoizedProps?.value;
        // Check for ref-based context with getValues method
        if (
          contextValue != null &&
          typeof contextValue === 'object' &&
          'getValues' in contextValue &&
          typeof contextValue.getValues === 'function'
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- validated at runtime
          return contextValue.getValues();
        }
        // Fallback to direct value for legacy contexts
        return contextValue;
      }
      currentFiber = LoggerUtil.traverseUpTheTree(currentFiber);
    }
    return undefined;
  },

  /**
   * Returns fiber node with custom attribute from react fiber
   * @param fiber react fiber
   * @param attribute custom attribute string
   * @returns fiber node with custom attribute or undefined
   */
  findFiberNodeWithCustomAttributeInFiber: (
    fiber: ElementNode | undefined,
    attributes: string[]
  ): ElementNode | undefined => {
    if (!fiber) {
      return undefined;
    }
    let currentFiber = fiber;
    while (currentFiber) {
      let hasAttribute = false;
      for (const attribute of attributes) {
        if (!isNil(currentFiber.memoizedProps?.[attribute])) {
          hasAttribute = true;
          break;
        }
      }
      if (hasAttribute) {
        return currentFiber;
      }
      currentFiber = LoggerUtil.traverseUpTheTree(currentFiber);
    }
    return undefined;
  },

  /**
   * Returns the parent element of the current element in the react fiber
   * @param element current element
   * @returns parent element or null
   */
  traverseUpTheTree: (currentFiber: ElementNode): ElementNode =>
    (currentFiber.return ?? currentFiber.sibling ?? currentFiber._debugOwner) as ElementNode,

  /**
   * Returns element with custom attribute
   * @param element element to search for custom attribute
   * @param reactFiber react fiber
   * @param attributes custom attributes
   * @returns element with custom attribute or null
   */
  getElementWithAttributes: (
    element: ElementNode,
    reactFiber: ElementNode | undefined,
    attributes: string[]
  ): ElementNode | null =>
    (attributes.some((attr) => element.hasAttribute(attr)) ? element : null) ||
    LoggerUtil.findFiberNodeWithCustomAttributeInFiber(reactFiber, attributes) ||
    element?.closest(attributes.join(', ')),

  // Sanitize HTML special characters
  sanitize: (value: string | undefined): string => value?.replace(/[<>"']/g, '') ?? '',
  /**
   * Returns element with custom attribute from react fiber
   * @param element element to search for custom attribute
   * @param reactFiber react fiber
   * @param attribute custom attribute string
   * @returns element with custom attribute or undefined
   */
  getElementAttributeFromTree: (
    element: ElementNode,
    reactFiber: ElementNode | undefined,
    attribute: string
  ): string | undefined =>
    (element.hasAttribute(attribute) ? element.getAttribute(attribute) : null) ||
    LoggerUtil.getAttributeValue(
      LoggerUtil.findFiberNodeWithCustomAttributeInFiber(reactFiber, [attribute]) || element?.closest(`[${attribute}]`),
      attribute
    ),

  /**
   * Checks if element is clickable
   * @param element element to check if it is clickable
   * @param reactFiber react fiber
   * @param interactiveElementSelectors list of interactive element selectors
   * @returns element is clickable or not
   */
  isClickableElement: (
    element: ElementNode,
    reactFiber: ElementNode | undefined,
    interactiveElementSelectors?: string[]
  ): boolean => {
    // check if generic data grid cell is clickable
    const isGridCellClickable = !!LoggerUtil.findFiberNodeWithCustomAttributeInFiber(reactFiber, ['isClickable'])
      ?.memoizedProps?.isClickable;
    const isValidClickHandlerPresent = Boolean(
      LoggerUtil.getAttributeValue(
        LoggerUtil.findFiberNodeWithCustomAttributeInFiber(reactFiber, ['onClick']) ?? null,
        'onClick'
      )
    );
    return (
      Boolean(
        element?.closest((interactiveElementSelectors?.length ? interactiveElementSelectors : ['*']).join(', '))
      ) ||
      isValidClickHandlerPresent ||
      isGridCellClickable
    );
  },

  /**
   * Returns attribute value from element
   * @param element element
   * @param attribute attribute string
   * @returns attribute value or empty string
   */
  getAttributeValue: (element: ElementNode | null, attribute: string): string =>
    (element?.memoizedProps?.[attribute] as string) || element?.getAttribute?.(attribute) || '',

  /**
   * Returns query param value from the URL
   * @param param query param name
   * @returns query param value
   */
  getParamFromUrl: (param: string): string => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param) || '';
  },

  /**
   * Checks if tracking is disabled
   * @param element element to check if tracking is disabled
   * @param reactFiber react fiber
   * @returns flag to indicate if tracking is disabled
   */
  isTrackingDisabled: (element: ElementNode, reactFiber: ElementNode | undefined): boolean => {
    const isDisabledElem = LoggerUtil.getElementWithAttributes(element, reactFiber, ['isDisabled']);
    const adsElementData = LoggerUtil.getElementWithAttributes(element, reactFiber, ['data']);
    const label = element.innerText;
    const foundAdsData = adsElementData?.stateNode?.props?.data
      ?.flat()
      ?.find((option: AuxElementCustomData) => option.label === label);

    return (
      LoggerUtil.getAttributeValue(
        LoggerUtil.getElementWithAttributes(element, reactFiber, [CustomLogAttribute.DISABLE_TRACKING]),
        CustomLogAttribute.DISABLE_TRACKING
      ) === 'true' ||
      Boolean(
        isDisabledElem?.isDisabled ??
          isDisabledElem?.stateNode?.isDisabled ??
          isDisabledElem?.stateNode?.props?.isDisabled
      ) ||
      !!foundAdsData?.eventData?.disableTrackEvent
    );
  },

  /**
   * Converts publish time to ISO string
   * @param eventLoggingDiagnostic eventLoggingDiagnostic object
   * @returns ISO string
   */
  getPublishTime: (eventLoggingDiagnostic: EventLoggingDiagnostic.AsObject | undefined): string | undefined => {
    const timeMills =
      (eventLoggingDiagnostic?.publishTime?.seconds ?? 0) * 1000 +
      (eventLoggingDiagnostic?.publishTime?.nanos ? eventLoggingDiagnostic.publishTime.nanos / 1000000 : 0);
    return timeMills ? new Date(timeMills).toISOString() : undefined;
  },

  /**
   * Post logs to AwBff server for splunk logging
   * @param eventQueue queue containing the events
   * @param appMetadata
   * @param sessionId
   * @param client
   * @param isPageHide whether this is triggered during page hide/unload
   * @returns
   * @tutorial wiki: https://webster.bfm.com/Wiki/display/AFWM/AW+Event+Logging+%28Web%29+-+Developer+Guide#AWEventLogging(Web)DeveloperGuide-SplunkLogging
   */
  postEventLog: (
    eventQueue: TrackingData[],
    appMetadata: AppMetadata,
    sessionId: string,
    client?: string,
    keepalive = false
  ): void => {
    const diagnosticInfo = getDiagnostic(
      appMetadata.name ?? '',
      appMetadata.version ?? '1.0.0',
      appMetadata.userId ?? '',
      'trackEvent'
    ).toObject();

    const payload: TrackingPayload[] = eventQueue.map((trackingData) => {
      const srcElement = trackingData.srcElement as ElementNode;
      const eventTypeKey =
        trackingData.elementInfo?.eventType?.toString() ??
        EventTypeMap[trackingData.srcEvent?.type ?? '']?.toString() ??
        EventType.EVENT_CLICK;
      const eventType = Object.keys(EventType)[Object.values(EventType).indexOf(Number(eventTypeKey))];
      return {
        app: {
          app: appMetadata.applicationNameFn?.() ?? (appMetadata.applicationName || AW_ADVISOR_APP_NAME),
          appDomain: LoggerUtil.getDomainName(),
          appMode: appMetadata.appMode,
          appTab: trackingData.activeTab,
          appView: trackingData.activeView,
          client,
          pageUri: window.location.href,
        },
        session: {
          sessionId,
          analysisDate: appMetadata.analysisDate ?? '',
          pkgName: appMetadata.pkg || LoggerUtil.getParamFromUrl('pkg'),
        },
        user: {
          internalUser: appMetadata.internalUserFn?.(),
          languagePreference: appMetadata.languagePreferenceFn?.(),
          showingMyBook: appMetadata.showingMyBook,
          viewAsId: appMetadata.viewAsIdFn?.(),
        },
        feature: {
          ...(trackingData.featureInfo ?? {}),
          featureName: trackingData.featureInfo?.featureName || LoggerUtil.getFeatureNameFromUrl(appMetadata),
        },
        domainSpecificInfo: trackingData.domainSpecificInfo ? trackingData.domainSpecificInfo : undefined,
        element: {
          elementId:
            trackingData.elementInfo?.elementId ||
            srcElement?.getAttribute('data-testid') ||
            srcElement?.id ||
            srcElement?.getAttribute('class') ||
            '',
          elementNameLocal: LoggerUtil.getElementNameLocal(trackingData, srcElement),
          elementName: LoggerUtil.getElementName(
            trackingData,
            LoggerUtil.getElementNameLocal(trackingData, srcElement)
          ),
          elementType: trackingData.elementInfo?.elementType || srcElement?.tagName,
          event: trackingData.elementInfo?.event || trackingData.action,
          eventType,
          elementValue: trackingData.elementInfo?.elementValue,
          metadata: LoggerUtil.computeMetadata(trackingData),
        },
        error: trackingData.error
          ? {
              errorInfo: trackingData.error?.errorInfo,
              errorStack: trackingData.error?.errorStackTrace,
              requestId: trackingData.error?.requestId,
            }
          : undefined,
        diagnosticInfo: {
          ...diagnosticInfo,
          operatingSystem:
            Object.keys(OperatingSystem)[Object.values(OperatingSystem).indexOf(diagnosticInfo.operatingSystem)],
          browserDiagnostic: JSON.stringify(diagnosticInfo.browserDiagnostic),
          publishTime: LoggerUtil.getPublishTime(diagnosticInfo),
          trace: JSON.stringify(diagnosticInfo.trace),
          dataCenter: JSON.stringify(diagnosticInfo.dataCenter),
        },
        ...(trackingData.queueTimestamp ? { queueTimestamp: trackingData.queueTimestamp } : {}),
      };
    });
    if (payload.length) {
      logPayload(payload, keepalive);
    }
  },

  /**
   * This function returns the internal hash from the URL
   * @param url URL to extract hash from
   * @param appMetadata app metadata
   * @returns internal hash
   */
  getHash: (url: string | null | undefined, appMetadata: AppMetadata) => {
    const hash = url?.split('#/').at(1);
    return appMetadata.toInternalPathFn ? appMetadata.toInternalPathFn(appMetadata.routeAliases, hash || '') : hash;
  },

  /**
   * Returns feature name from the URL
   * @returns feature name, e.g. '#/risk-monitor/accounts' -> 'risk-monitor'
   */
  getFeatureNameFromUrl: (appMetadata: AppMetadata): string => {
    // convert to internal if configured
    const hash = LoggerUtil.getHash(window.location.href, appMetadata);
    return hash?.split('/').at(0) ?? '';
  },

  /**
   * Get sub domain name from the URL, e.g. xyz.blackrock.com -> XYZ
   * This is useful to grab the external domain names
   * @returns string
   */
  getDomainName: (): string => window.location.hostname.split('.')[0].toUpperCase(),

  /**
   * Sets up the sanitization of the Excel export file name and cell values.
   * This function wraps the original exportDataAsExcel method to sanitize sensitive data.
   */
  setupExcelExportSanitization: (api: GridApi): void => {
    if (api && typeof api.exportDataAsExcel === 'function') {
      const originalExportDataAsExcel = api.exportDataAsExcel.bind(api);
      api.exportDataAsExcel = function (params?: ExcelExportParams) {
        // Ensure params is defined
        if (!params) {
          params = {};
        }
        // Sanitize the file name to remove sensitive data
        if (typeof params.fileName === 'string') {
          params.fileName = params.fileName.replace(SENSITIVE_DATA_BRANDED_TYPE, '');
        }
        // Wrap the processCellCallback to sanitize cell values
        //eslint-disable-next-line @typescript-eslint/unbound-method -- storing the original function
        const originalProcessCellCallback = params.processCellCallback;
        params.processCellCallback = (cellParams: ProcessCellForExportParams) => {
          let value = cellParams.value;
          // If the value is undefined or null, return an empty string
          if (isNil(value)) {
            return '';
          }
          // Execute the original processCellCallback if it exists
          if (typeof originalProcessCellCallback === 'function') {
            value = originalProcessCellCallback(cellParams);
          }
          // Replace sensitive data branded string with an empty string
          if (typeof value === 'string') {
            value = value.replace(SENSITIVE_DATA_BRANDED_TYPE, '');
          }
          //eslint-disable-next-line  @typescript-eslint/no-unsafe-return -- cellParams.value has type any
          return value;
        };
        // Call the original exportDataAsExcel method with sanitized params
        originalExportDataAsExcel.call(this, params);
      };
    }
  },
  /**
   * Sets the partner source for the given event if it exists.
   * @param trackingData - The TrackingData to set the partner source for.
   * @param appMetadata - The AppMetadata containing the partner source.
   */
  setPartnerSource: (trackingData: TrackingData, appMetadata: AppMetadata): void => {
    if (appMetadata.partnerSource) {
      LoggerUtil.setDomainSpecificAdditionalAttribute(trackingData, 'partner_source', appMetadata.partnerSource);
    }
  },
  /**
   * Sets a domain-specific additional attribute for the given event.
   * @param trackingData - The TrackingData to set the domain-specific additional attribute for.
   * @param attribute - The attribute name.
   * @param value - The attribute value.
   */
  setDomainSpecificAdditionalAttribute: (trackingData: TrackingData, attribute: string, value: string): void => {
    trackingData.domainSpecificInfo = {
      ...trackingData.domainSpecificInfo,
      additionalAttributes: {
        ...trackingData.domainSpecificInfo?.additionalAttributes,
        [attribute]: value,
      },
    };
  },
  /**
   * Sets the web entity ID and web tracking ID for the given event if they exist and Adobe Analytics is enabled.
   * @param trackingData - The TrackingData to set the web entity ID and web tracking ID for.
   * @param appMetadata - The AppMetadata containing the web entity ID and web tracking ID.
   */
  setWebEntityAndTrackingId: (trackingData: TrackingData, appMetadata: AppMetadata): void => {
    // Set this only when adobe analytics is enabled for the package
    if (appMetadata.adobeConfig?.enableAdobeAnalytics) {
      if (!!appMetadata.webEntityId) {
        LoggerUtil.setDomainSpecificAdditionalAttribute(trackingData, 'webEntityId', appMetadata.webEntityId);
      }
      if (!!appMetadata?.webTrackingId && appMetadata.webTrackingId !== appMetadata.userId) {
        LoggerUtil.setDomainSpecificAdditionalAttribute(trackingData, 'webTrackingId', appMetadata.webTrackingId);
      }
    }
  },
};

export const truncateStackTrace = (stack: string | undefined, maxFrames = 25): string | undefined => {
  if (!stack) {
    return stack;
  }
  return stack
    .split('\n')
    .slice(0, maxFrames + 1)
    .join('\n');
};
